import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePreviewState } from '../DJPal/DJPalAudioPreview';
import { getCachedDecoded } from '../../utils/peaksCache';
import {
    useListenProgress, useIsCurrentTrack, isHeard, getPlayerClock, seekPlayer,
    requestResume, summarizePeaks, formatClock,
} from '../../utils/listenProgress';
import { track } from '../../utils/analytics';

// DESIGN V2 — what a For You hero card knows about your listening:
//   now playing → a live waveform along the card (click to seek), EQ kicker
//   heard       → the waveform stays, frozen and dimmed where you stopped;
//                 a finished listen gets a check by the kicker
//   unheard     → nothing (the card stays as clean as before)
// The listen memory itself lives in utils/listenProgress.js.

// One hook for the card: is it live, is it playing, what was heard.
export function useHeroListen(listen) {
    const trackId = listen?.trackId ?? null;
    const preview = usePreviewState(listen?.previewId);
    const isCurrent = useIsCurrentTrack(listen ? trackId : null, listen?.src || null);
    const reduxPlaying = useSelector((s) => s.musicPlayer.isPlaying);
    const rec = useListenProgress(listen ? trackId : null);
    if (!listen) return { active: false };
    const playing = !!preview.playing || (isCurrent && !!reduxPlaying);
    const heard = isHeard(rec);
    const done = heard && !!rec.done;
    const canResume = heard && !done && !isCurrent && rec.l > 5 && rec.l < rec.d - 5;
    return {
        active: true, trackId, previewId: listen.previewId, origin: listen.origin,
        playing, loading: !!preview.loading, isCurrent, rec, heard, done, canResume,
        showWave: isCurrent || heard,
    };
}

const PlayGlyph = () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20 7 4" /></svg>;
const PauseGlyph = () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="3" width="5" height="18" rx="1" /><rect x="14" y="3" width="5" height="18" rx="1" /></svg>;

export const EqBars = () => (
    <span className="v2-hero-eq" aria-hidden="true"><span /><span /><span /></span>
);
export const HeardCheck = () => (
    <svg className="v2-hero-heard" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-label="Heard"><polyline points="5 12.5 10 17.5 19 7" /></svg>
);

// The hero card's play control (lead = the filled button, else the amber
// disc). Mirrors the row it plays and offers Resume for a half-heard track.
export function HeroListenPlayButton({ listen, lead }) {
    const { playing, loading, canResume, rec, trackId, previewId, origin } = useHeroListen(listen);
    const onClick = (e) => {
        e.stopPropagation();
        if (canResume && !playing) {
            requestResume(trackId, rec.l, rec.d);
            track('feed_hero_resumed', { origin, position_sec: Math.round(rec.l), duration_sec: Math.round(rec.d) });
        }
        window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId }));
    };
    const label = playing ? 'Pause' : (canResume ? `Resume ${formatClock(rec.l)}` : 'Play');
    if (lead) {
        return (
            <button type="button" className={`v2-btn primary${playing ? ' playing' : ''}`} title={label} onClick={onClick} aria-busy={loading || undefined}>
                {loading ? <span className="mix-play-spinner" aria-hidden="true" /> : (playing ? <PauseGlyph /> : <PlayGlyph />)}
                <span>{label}</span>
            </button>
        );
    }
    return (
        <button type="button" className="v2-btn icon" title={label} onClick={onClick} aria-busy={loading || undefined}>
            {playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
    );
}

function readColors(el) {
    const css = getComputedStyle(el);
    return {
        played: css.getPropertyValue('--color-accent').trim() || 'currentColor',
        rest: css.getPropertyValue('--v2-hero-line').trim() || css.getPropertyValue('--v2-on-art-line').trim() || 'currentColor',
    };
}

// The waveform strip. Bars come from the stored summary, else the shared
// peaks cache (the bottom player fills it while the track plays — this never
// starts its own download); with neither it is a plain progress line.
export function HeroWave({ state }) {
    const { isCurrent, rec, trackId, previewId, origin } = state;
    const canvasRef = useRef(null);
    const [bars, setBars] = useState(() => rec?.b || null);
    const ratioRef = useRef(rec && rec.d > 0 ? rec.p / rec.d : 0);

    // Bars: stored summary first; while live, the player's peaks once cached.
    useEffect(() => {
        if (rec?.b) { setBars(rec.b); return undefined; }
        if (!isCurrent) return undefined;
        const fromCache = () => {
            const key = window.__currentPlayerOriginalUrl || window.__currentPlayerMusicSrc;
            const entry = key ? getCachedDecoded(key) : null;
            if (entry?.peaks) setBars(summarizePeaks(entry.peaks));
        };
        fromCache();
        window.addEventListener('wf-peaks-cached', fromCache);
        return () => window.removeEventListener('wf-peaks-cached', fromCache);
    }, [rec?.b, isCurrent]);

    // Paint, and while this is the player's track follow its clock per frame.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        let raf = null;
        let lastDrawn = -1;
        let lastW = 0;
        const draw = (ratio) => {
            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            if (!w || !h) return;
            const dpr = window.devicePixelRatio || 1;
            if (lastW !== w) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); lastW = w; }
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            // roundRect is Chromium 99 / Safari 16; older webviews get square bars.
            const rr = (x, y, rw, rh, r) => { ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(x, y, rw, rh, r); else ctx.rect(x, y, rw, rh); ctx.fill(); };
            ctx.clearRect(0, 0, w, h);
            const { played, rest } = readColors(canvas);
            const cut = ratio * w;
            if (!bars) {
                // No peaks yet: a rounded progress line through the middle.
                const y = h / 2 - 1.5;
                ctx.fillStyle = rest; rr(0, y, w, 3, 1.5);
                if (cut > 0) { ctx.fillStyle = played; rr(0, y, Math.max(3, cut), 3, 1.5); }
                return;
            }
            const step = 4; // 2px bar + 2px gap, as the list waveforms
            const n = Math.max(1, Math.floor(w / step));
            for (let i = 0; i < n; i++) {
                const v = bars[Math.min(bars.length - 1, Math.floor((i / n) * bars.length))] / 99;
                const bh = Math.max(2, v * h);
                const x = i * step;
                ctx.fillStyle = x + 1 < cut ? played : rest;
                rr(x, (h - bh) / 2, 2, bh, 1);
            }
        };
        const frozen = rec && rec.d > 0 ? Math.min(1, rec.p / rec.d) : 0;
        if (!isCurrent) { ratioRef.current = frozen; draw(frozen); }
        else {
            const loop = () => {
                const c = getPlayerClock();
                const r = c && c.d > 0 ? Math.min(1, c.t / c.d) : ratioRef.current;
                if (Math.abs(r - lastDrawn) > 0.0005) { draw(r); lastDrawn = r; ratioRef.current = r; }
                raf = requestAnimationFrame(loop);
            };
            loop();
        }
        const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => { lastW = 0; draw(ratioRef.current); }) : null;
        if (ro) ro.observe(canvas);
        return () => { if (raf) cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
    }, [bars, isCurrent, rec?.p, rec?.d]); // eslint-disable-line react-hooks/exhaustive-deps

    const onClick = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        if (isCurrent) {
            const c = getPlayerClock();
            if (c && c.d > 0) seekPlayer(ratio * c.d);
        } else if (rec && rec.d > 0) {
            // A heard track: play it from where you clicked.
            requestResume(trackId, ratio * rec.d, rec.d);
            window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId }));
        }
        track('feed_hero_seeked', { origin, live: !!isCurrent, ratio: Math.round(ratio * 100) / 100 });
    };

    return (
        <div className={`v2-hero-wave${isCurrent ? ' live' : ''}`} onClick={onClick} role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratioRef.current * 100)}>
            <canvas ref={canvasRef} />
            {!isCurrent && rec && rec.d > 0 && !rec.done && (
                <span className="v2-hero-wave-time">{formatClock(rec.p)} / {formatClock(rec.d)}</span>
            )}
        </div>
    );
}
